import { ensureFreshToken, getIdToken, type IdentityInfo } from './signin';
/**
 * The single seam between the browser and the API.
 *
 * Every request carries `x-pts-user` and `x-pts-role` from a module-level store,
 * because the demo resolves identity from headers rather than standing up
 * Cognito. Swapping this for a bearer token is the whole of the production
 * change — nothing above this file knows how the principal is carried.
 *
 * A failing request throws `ApiError`, which keeps the status, the server's own
 * message and any `findings`, `blockers` or `checks` array the route returned.
 * Those arrays are the difference between "issue was blocked" and "issue was
 * blocked because two sense checks are red, and here is what would clear them",
 * so they are never discarded.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ApiErrorBody, IssueCheck, Role, ValidationFinding } from './types';

const BASE = '/api/v1';
const STORAGE_KEY = 'pts-compass.current-user';

// ------------------------------------------------------------------ current user

export interface CurrentUser {
  /** The demo user id, e.g. "ben". Sent as x-pts-user. */
  readonly userId: string;
  /** The role being exercised. Sent as x-pts-role. */
  readonly role: Role;
  readonly displayName?: string;
}

const DEFAULT_USER: CurrentUser = {
  userId: 'ben',
  role: 'commercial_administrator',
  displayName: 'Ben Fox',
};

const KNOWN_ROLES: Role[] = [
  'commercial_administrator',
  'commercial_user',
  'engineer',
  'approver',
  'read_only',
];

function readStoredUser(): CurrentUser {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_USER;
    const parsed = JSON.parse(raw) as Partial<CurrentUser>;
    if (typeof parsed?.userId !== 'string' || !parsed.userId) return DEFAULT_USER;
    if (typeof parsed?.role !== 'string' || !KNOWN_ROLES.includes(parsed.role as Role)) {
      return DEFAULT_USER;
    }
    return {
      userId: parsed.userId,
      role: parsed.role as Role,
      ...(typeof parsed.displayName === 'string' ? { displayName: parsed.displayName } : {}),
    };
  } catch {
    // Private browsing, blocked site data, or a corrupted value. Fall back rather
    // than leaving the app without an identity.
    return DEFAULT_USER;
  }
}

let currentUser: CurrentUser = readStoredUser();
const userListeners = new Set<() => void>();

export function getCurrentUser(): CurrentUser {
  return currentUser;
}

/**
 * Change who the demo is signed in as. Persists, then notifies every subscriber
 * so that hooks built on `useApi` re-fetch against the new role — the interface
 * must reflect what the server will actually allow, not a cached view of it.
 */
export function setCurrentUser(next: CurrentUser): void {
  if (next.userId === currentUser.userId && next.role === currentUser.role) return;
  currentUser = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage is a convenience. The session still works without it.
  }
  for (const listener of userListeners) listener();
}

export function subscribeToUser(listener: () => void): () => void {
  userListeners.add(listener);
  return () => {
    userListeners.delete(listener);
  };
}

/** React binding for the current user. Re-renders when the role switcher moves. */
export function useCurrentUser(): CurrentUser {
  return useSyncExternalStore(subscribeToUser, getCurrentUser, getCurrentUser);
}

// ------------------------------------------------------------------ errors

/**
 * A failed API call. `findings` and `checks` come straight from the server and
 * carry the resolution text; render them rather than the bare message.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly findings: ValidationFinding[];
  readonly checks: IssueCheck[];
  readonly requestId: string | null;
  readonly body: unknown;

  constructor(
    status: number,
    message: string,
    options: {
      findings?: ValidationFinding[];
      checks?: IssueCheck[];
      requestId?: string | null;
      body?: unknown;
    } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.findings = options.findings ?? [];
    this.checks = options.checks ?? [];
    this.requestId = options.requestId ?? null;
    this.body = options.body;
  }

  /** True where the server refused on authorisation rather than on data. */
  get isForbidden(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** True where something moved underneath this request — a stale hash, a published release. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** True where the error brought its own explanation of what would clear it. */
  get hasDetail(): boolean {
    return this.findings.length > 0 || this.checks.length > 0;
  }
}

function isFindingArray(value: unknown): value is ValidationFinding[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'object' && v !== null && 'severity' in v);
}

function isCheckArray(value: unknown): value is IssueCheck[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'object' && v !== null && 'passed' in v);
}

// ------------------------------------------------------------------ requests

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface RequestOptions {
  /** Aborts the request when the caller unmounts or supersedes it. */
  signal?: AbortSignal;
  /** Extra headers. The identity headers are always added and cannot be overridden. */
  headers?: Record<string, string>;
  /** Query string parameters. Undefined and null values are dropped. */
  query?: Record<string, string | number | boolean | null | undefined>;
}

function url(path: string, query?: RequestOptions['query']): string {
  const full = path.startsWith('/api/') ? path : `${BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return full;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined) continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${full}?${qs}` : full;
}

function identityHeaders(): Record<string, string> {
  return {
    'x-pts-user': currentUser.userId,
    'x-pts-role': currentUser.role,
  };
}

/** The identity as the app first learned it; set once so requests can renew a token in the background. */
let identityForRefresh: IdentityInfo | null = null;
export function rememberIdentity(info: IdentityInfo): void {
  identityForRefresh = info;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  await ensureFreshToken(identityForRefresh);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers ?? {}),
    ...identityHeaders(),
    // With the identity service on, the ID token is the credential and the two
    // demo headers are ignored by the API.
    ...(getIdToken() ? { Authorization: `Bearer ${getIdToken()}` } : {}),
  };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    const init: RequestInit = { method, headers };
    if (body !== undefined) init.body = JSON.stringify(body);
    if (options.signal) init.signal = options.signal;
    response = await fetch(url(path, options.query), init);
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      0,
      'The API did not respond. Check that the PTS Compass API is running on port 4000.',
      { body: cause },
    );
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      parsed = text;
    }
  }

  if (!response.ok) {
    const errorBody = (typeof parsed === 'object' && parsed !== null ? parsed : {}) as ApiErrorBody;
    const findings = isFindingArray(errorBody.findings)
      ? errorBody.findings
      : isFindingArray(errorBody.blockers)
        ? errorBody.blockers
        : [];
    throw new ApiError(
      response.status,
      typeof errorBody.error === 'string' && errorBody.error
        ? errorBody.error
        : `${response.status} ${response.statusText}`,
      {
        findings,
        checks: isCheckArray(errorBody.checks) ? errorBody.checks : [],
        requestId: errorBody.requestId ?? null,
        body: parsed,
      },
    );
  }

  return parsed as T;
}

export function get<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>('GET', path, undefined, options);
}

export function post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>('POST', path, body ?? {}, options);
}

export function patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>('PATCH', path, body ?? {}, options);
}

export function put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return request<T>('PUT', path, body ?? {}, options);
}

export function del<T>(path: string, options?: RequestOptions): Promise<T> {
  return request<T>('DELETE', path, undefined, options);
}

/**
 * Prefixed aliases. `get` and `post` are common enough words that a screen
 * importing several helpers reads better with the prefix, so both spellings are
 * exported and they are the same function.
 */
export const apiGet = get;
export const apiPost = post;
export const apiPatch = patch;
export const apiPut = put;
export const apiDelete = del;

/** A DELETE that carries a body — a reason, a confirmation — which `del` deliberately does not. */
export function delWithBody<T>(path: string, body: unknown, options?: RequestOptions): Promise<T> {
  return request<T>('DELETE', path, body, options);
}

/**
 * Fetch a route that returns something other than JSON — the document renderer
 * returns HTML. Errors still come back as `ApiError`.
 */
/** A file (a Word document) fetched with the same credential, for the browser to save. */
export async function getBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  await ensureFreshToken(identityForRefresh);
  const response = await fetch(url(path, options.query), {
    method: 'GET',
    headers: { ...(options.headers ?? {}), ...identityHeaders(), ...(getIdToken() ? { Authorization: `Bearer ${getIdToken()}` } : {}) },
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try { message = ((await response.json()) as { error?: string }).error ?? message; } catch { /* keep the status */ }
    throw new ApiError(response.status, message, {});
  }
  return response.blob();
}

export async function getText(path: string, options: RequestOptions = {}): Promise<string> {
  // The same credential as every other call. Without the token a document
  // fetch on the live site came back "Not signed in" and the window closed.
  await ensureFreshToken(identityForRefresh);
  const init: RequestInit = {
    method: 'GET',
    headers: {
      Accept: 'text/html,application/json',
      ...(options.headers ?? {}),
      ...identityHeaders(),
      ...(getIdToken() ? { Authorization: `Bearer ${getIdToken()}` } : {}),
    },
  };
  if (options.signal) init.signal = options.signal;
  const response = await fetch(url(path, options.query), init);
  const text = await response.text();
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = JSON.parse(text) as ApiErrorBody;
      if (typeof body.error === 'string') message = body.error;
    } catch {
      // Not JSON. Keep the status line.
    }
    throw new ApiError(response.status, message, { body: text });
  }
  return text;
}

/**
 * A URL a browser can open directly — an anchor href or an iframe src.
 *
 * Direct navigation cannot carry the identity headers, so the demo user and role
 * ride as query parameters instead. Nothing sensitive is in them: they name the
 * demo principal, which the server re-checks on every request anyway.
 */
export function documentUrl(documentId: string): string {
  return url(`/documents/${documentId}`, {
    'x-pts-user': currentUser.userId,
    'x-pts-role': currentUser.role,
  });
}

// ------------------------------------------------------------------ hooks

export interface ApiState<T> {
  data: T | null;
  error: ApiError | null;
  loading: boolean;
  /** Re-runs the request against the current identity. */
  reload: () => void;
}

/**
 * Fetch a GET route and keep it in step with the role switcher.
 *
 * Pass `null` as the path to hold off — useful while a route parameter is still
 * being resolved. Every change of signed-in user re-fetches, because a different
 * role sees different figures and hiding that would be a lie about the data.
 */
export function useApi<T>(path: string | null, deps: readonly unknown[] = []): ApiState<T> {
  const user = useCurrentUser();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(path !== null);
  const [nonce, setNonce] = useState(0);
  const latest = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (path === null) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    const ticket = ++latest.current;
    const controller = new AbortController();
    setLoading(true);
    get<T>(path, { signal: controller.signal })
      .then((result) => {
        if (ticket !== latest.current) return;
        setData(result);
        setError(null);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted || ticket !== latest.current) return;
        setData(null);
        setError(
          cause instanceof ApiError
            ? cause
            : new ApiError(0, cause instanceof Error ? cause.message : String(cause), { body: cause }),
        );
        setLoading(false);
      });
    return () => controller.abort();
    // The identity is a real dependency: a different role gets a different body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, user.userId, user.role, ...deps]);

  return { data, error, loading, reload };
}

/**
 * Run a write and track its progress, without the caller writing the same four
 * pieces of state every time. `run` resolves with the result and rejects with
 * the `ApiError`, so a caller that wants to branch on the failure still can.
 */
export interface MutationState<TArgs extends unknown[], TResult> {
  run: (...args: TArgs) => Promise<TResult>;
  pending: boolean;
  error: ApiError | null;
  /** Clears a failure without re-running, e.g. when a drawer is closed. */
  reset: () => void;
}

export function useMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): MutationState<TArgs, TResult> {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args: TArgs): Promise<TResult> => {
      setPending(true);
      setError(null);
      try {
        const result = await fn(...args);
        if (alive.current) setPending(false);
        return result;
      } catch (cause) {
        const apiError =
          cause instanceof ApiError
            ? cause
            : new ApiError(0, cause instanceof Error ? cause.message : String(cause), { body: cause });
        if (alive.current) {
          setError(apiError);
          setPending(false);
        }
        throw apiError;
      }
    },
    [fn],
  );

  const reset = useCallback(() => setError(null), []);

  return { run, pending, error, reset };
}
